/* AddZoneRenderer.js */

"use strict";
const uiCore = require("@gluenetworks/steve_ui_core");
const ButtonControl = uiCore.Control.Action.Button;
const InputTextControl = uiCore.Control.Textfield.InputText;

const MODAL_TITLE_ADD = "Add New Zone";
const DESCRIPTION = "You are about to create a new zone for this organization." +
    "This zone will remain in an unassigned state until a new engine is started for this " +
    "zone.  Please install engines against this zone to activate the zone."

const LICENSE_WARNING = "WARNING - Best practices recommend that zones be created at the same " +
    "organization level as licensing so that zones may be shared across " +
    "the licensed organization hierachy. It does not appear that licensing is installed " +
    "at this organization level. While creating a zone at this level is allowed, it is not recommended."

class AddZoneRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = this._createRootElement();

        this._errorContainer = document.createElement("div");
        this._errorContainer.classList.add("error");

        /* form controls */
        this.zoneNameText = new InputTextControl({
            darkTheme: true,
            invalid: false,
            placeholder: "Name"
        });
        this.displayNameText = new InputTextControl({
            darkTheme: true,
            invalid: false,
            placeholder: "Display Name"
        });
        this.cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
            disabled: false
        });
        this.saveButton = new ButtonControl({
            label: "Save",
            darkTheme: true,
            disabled: true
        });

        this._registerListeners();
        this.render();
    }

    get rootElement() {
        return this.getRootElement();
    }

    get model() {
        return this.getModel();
    }

    /*******************************************/
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    render() {
        if (this._renderProm === null) {
            return Promise.resolve();
        }
        this._renderProm = this._renderProm || this._render();
        return this._renderProm;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    getRootElement() {
        return this._rootElem;
    }

    getModel() {
        return this._model;
    }

    /********************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS */
    /* START PRIVATE COMPONENT RENDERER METHODS */

    /********************************************/

    _registerListeners() {
        this.zoneNameText.on("UserValue", this._onNameChange.bind(this));
        this.displayNameText.on("UserValue", this._onDisplayNameChange.bind(this));
        this.saveButton.on("UserClick", this.submitAction.bind(this));
        this.cancelButton.on("UserClick", this.cancelAction.bind(this));
        this._model.on("Error", this._onError.bind(this));
    }

    _unregisterListeners() {
        this.zoneNameText.removeAllListeners("UserValue");
        this.displayNameText.removeAllListeners("UserValue");
        this.saveButton.removeAllListeners("UserClick");
        this.cancelButton.removeAllListeners("UserClick");
        this._model.removeAllListeners("Error");
    }

    _render() {
        const root = document.createDocumentFragment();
        const title = document.createElement("h2");
        title.innerText = MODAL_TITLE_ADD;

        const description = document.createElement("div");
        description.innerHTML = DESCRIPTION;

        root.appendChild(title);
        root.appendChild(description);
        if (!this.model.orgHasLicense) {
            const warning = document.createElement("div");
            warning.classList.add("license-warning");
            warning.innerHTML = LICENSE_WARNING;
            root.appendChild(warning);
        }
        root.appendChild(this._createForm());
        root.appendChild(this._errorContainer);

        return new Promise((resolve /*,reject*/) => {
            this._rootElem.appendChild(root);
            resolve();
        });
    }

    _shutdown() {
        this._unregisterListeners();
        this._rootElem.remove();
        this._rootElem = null;

        return Promise.all([
            this.zoneNameText.shutdown(),
            this.displayNameText.shutdown(),
            this.cancelButton.shutdown(),
            this.saveButton.shutdown()
        ])
            .then(() => {
                this._model = null;
                this._shutdownProm = null;
                this._renderProm = null;
                this.zoneNameText = undefined;
                this.displayNameText = undefined;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PRIVATE RENDERER METHODS */

    /*******************************************/

    _createRootElement() {
        const rootElement = document.createElement("div");
        rootElement.classList.add("add-zone", "modal_feedback_container");
        rootElement.component = this._model;
        return rootElement;
    }

    _createForm() {
        const formRoot = document.createDocumentFragment();
        const zoneNameLabel = document.createElement("label");
        zoneNameLabel.innerText = "Name";
        zoneNameLabel.title = "One-word internal name for the zone";

        const displayNameLabel = document.createElement("label");
        displayNameLabel.innerText = "Display Name";

        const actionsContainer = document.createElement("div");
        actionsContainer.classList.add("actions-container");
        actionsContainer.appendChild(this.cancelButton.renderer.getRootElement());
        actionsContainer.appendChild(this.saveButton.renderer.getRootElement());

        formRoot.appendChild(zoneNameLabel);
        formRoot.appendChild(this.zoneNameText.renderer.getRootElement());
        formRoot.appendChild(displayNameLabel);
        formRoot.appendChild(this.displayNameText.renderer.getRootElement());
        formRoot.appendChild(actionsContainer);

        return formRoot;
    }

    /*******************************************/
    /* END PRIVATE RENDERER METHODS  */
    /* START HANDLER METHODS */
    /*******************************************/
    _onNameChange() {
        this._checkSaveButton();
    }
    _onDisplayNameChange() {
        this._checkSaveButton();
    }

    _onError(errorMsg) {
        this._setError(errorMsg);
        this._checkSaveButton(false);
    }

    submitAction() {
        const data = this._getFormData();
        this._model.createEngineZone(data);
    }

    cancelAction() {
        this._model.closePopUp();
    }
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _getFormData() {
        const formData = {
            name: this.zoneNameText.value,
            displayName: this.displayNameText.value,
        };

        return formData;
    }

    _checkSaveButton(state = null) {
        const data = this._getFormData();
        const saveIsEnabled =
            state === null ? [data.name, data.displayName].every(Boolean) : state;

        this.saveButton.setDisabled(!saveIsEnabled);
    }

    _setError(error) {
        let errorStr = 'Error';
        if (typeof error === 'string') {
            errorStr = error;
        } else if (error instanceof Error) {
            errorStr = error.toString();
        } else if (error && error.errorMsg) {
            const errorCode = error.errorCode || "Error";
            const errorMsg = error.errorMsg;
            errorStr = `${errorCode}: ${errorMsg}`;

            //specific error states
            if(errorStr.includes('duplicate key error collection')) {
                errorStr = 'There\'s already a zone with that name. Choose another name.';
            }
        }
        this._errorContainer.innerText = errorStr;
    }

    _clearError() {
        this._errorContainer.innerText = "";
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = AddZoneRenderer;
